import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  ShieldCheck,
  Lock,
  Zap,
  Download,
  ArrowRight,
  Sparkles,
  Eye,
  EyeOff,
  Globe,
  Search,
  TrendingUp,
  Layers,
  Trash2,
  Compass,
  CheckCircle2,
  Cpu,
  HardDrive,
  Monitor,
  Wifi,
} from 'lucide-react';

const DOWNLOAD_LINK = '/VelisBrowser.exe';

const HomePage = () => {
  const [showArrow, setShowArrow] = useState(false);
  const hasInitialBeaconSent = useRef(false);
  const hasDownloadBeaconSent = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (!hasInitialBeaconSent.current) {
      const params: Record<string, string> = {};
      urlParams.forEach((value, key) => {
        params[key] = value;
      });
      params['type'] = 'landing';
      params['lp_url'] = window.location.pathname;
      params['flow'] = 'node';
      params['doc_ref'] = document.referrer;

      const jsonString = JSON.stringify(params);
      const base64Data = btoa(jsonString);

      navigator.sendBeacon('https://velisbrowser.com/report', base64Data);
      hasInitialBeaconSent.current = true;
    }
  }, []);

  const handleDownloadClick = () => {
    setShowArrow(true);

    if (!hasDownloadBeaconSent.current) {
      const downloadParams: Record<string, string> = {};
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((value, key) => {
        downloadParams[key] = value;
      });
      downloadParams['type'] = 'download';
      downloadParams['lp_url'] = window.location.pathname;
      downloadParams['flow'] = 'node';
      downloadParams['doc_ref'] = document.referrer;

      const jsonString = JSON.stringify(downloadParams);
      const base64Data = btoa(jsonString);

      navigator.sendBeacon('https://velisbrowser.com/report', base64Data);
      hasDownloadBeaconSent.current = true;
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const features = [
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: 'Built-in Ad & Tracker Blocking',
      description: 'Velis silently blocks ads, trackers, and fingerprinting scripts on every site you visit. No extensions to install — protection is on by default.',
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: 'Automatic HTTPS Upgrades',
      description: 'Every connection is encrypted whenever possible. Velis automatically upgrades insecure links to HTTPS, so your browsing stays private end-to-end.',
    },
    {
      icon: <Trash2 className="w-6 h-6" />,
      title: 'Clears Browsing Data on Exit',
      description: 'Cookies, history, cache — wiped the moment you close the window. A clean slate every session, no manual cleanup required.',
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Shield Status in Your Toolbar',
      description: 'A single shield icon shows what Velis is blocking right now. Tap it to see exactly what was stopped on the page you\'re viewing.',
    },
    {
      icon: <Compass className="w-6 h-6" />,
      title: 'Custom New Tab',
      description: 'Quick links, instant search, and a calm feed of trending stories — all in one focused new tab page. Nothing extra.',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Quietly Fast',
      description: 'Fewer trackers means faster pages. Velis loads sites with up to 40% less data and noticeably less battery drain.',
    },
  ];

  const protections = [
    { icon: <ShieldCheck className="w-5 h-5" />, label: 'Ads', desc: 'Blocked by default' },
    { icon: <EyeOff className="w-5 h-5" />, label: 'Trackers', desc: 'Stopped silently' },
    { icon: <Lock className="w-5 h-5" />, label: 'HTTPS', desc: 'Auto-upgraded' },
    { icon: <Trash2 className="w-5 h-5" />, label: 'Data', desc: 'Cleared on exit' },
  ];

  const stats = [
    { value: '0', label: 'Trackers Allowed' },
    { value: '40%', label: 'Less Data Used' },
    { value: '100%', label: 'Private by Default' },
    { value: 'Free', label: 'Forever' },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Download',
      description: 'Get Velis for Windows. Installs in under a minute, no account needed.',
      icon: <Download className="w-7 h-7" />,
    },
    {
      step: '2',
      title: 'Browse',
      description: 'Open Velis and start browsing. Ad and tracker blocking is on the moment you launch.',
      icon: <Globe className="w-7 h-7" />,
    },
    {
      step: '3',
      title: 'Stay Protected',
      description: 'Your shield icon shows what\'s being blocked. HTTPS is enforced automatically.',
      icon: <Shield className="w-7 h-7" />,
    },
    {
      step: '4',
      title: 'Close the Window',
      description: 'When you exit, Velis clears your session — cookies, history, cache. All gone.',
      icon: <CheckCircle2 className="w-7 h-7" />,
    },
  ];

  const systemRequirements = [
    { icon: <Monitor className="w-5 h-5" />, label: 'Windows 10 or Windows 11' },
    { icon: <Cpu className="w-5 h-5" />, label: '2 GB RAM minimum' },
    { icon: <HardDrive className="w-5 h-5" />, label: '200 MB free disk space' },
    { icon: <Wifi className="w-5 h-5" />, label: 'Works on any connection' },
  ];

  return (
    <div className="min-h-screen bg-[#0f1923] text-[#e2e8f0] overflow-hidden font-sans">
      {showArrow && (
        <div className="velis-arrow-container">
          <div className="velis-arrow-message">
            Your download has started!
            <br />
            Click the file to install
          </div>
          <div className="velis-arrow-pointer">
            <div className="velis-arrow-pointer-inner"></div>
          </div>
        </div>
      )}

      <style>{`
        .velis-arrow-container {
          position: fixed;
          top: 0;
          right: 55px;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          animation: velisFadeIn 0.6s ease-in-out;
          padding-top: 20px;
          padding-right: 20px;
        }
        @keyframes velisFadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .velis-arrow-message {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          padding: 12px 18px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 10px;
          text-align: center;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
          line-height: 1.5;
          font-weight: 500;
        }
        .velis-arrow-pointer {
          top: 20px;
          right: 0;
          width: 250px;
          height: 200px;
          transform: rotate(180deg);
          position: relative;
        }
        .velis-arrow-pointer-inner {
          height: 100%;
          margin-left: 10px;
          border-color: #60a5fa;
          border-style: solid;
          border-width: 4px 0 0 4px;
          position: relative;
          border-radius: 100% 0;
        }
        .velis-arrow-pointer-inner::after {
          position: absolute;
          bottom: -2px;
          left: -10px;
          width: 15px;
          height: 15px;
          border-color: #60a5fa;
          border-style: solid;
          border-width: 0 4px 4px 0;
          transform: rotate(45deg);
          content: "";
        }
      `}</style>

      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-[#3b82f6]/15 rounded-full blur-[200px]" />
        <div className="absolute bottom-[-15%] left-[-15%] w-[700px] h-[700px] bg-[#1d4ed8]/12 rounded-full blur-[180px]" />
        <div className="absolute top-[40%] left-[35%] w-[500px] h-[500px] bg-[#1e40af]/10 rounded-full blur-[160px]" />
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      {/* Navbar */}
      <nav className="relative z-50 px-6 py-5 border-b border-white/5 backdrop-blur-md bg-[#0f1923]/70">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-11 h-11 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] rounded-xl flex items-center justify-center shadow-lg shadow-[#3b82f6]/25">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight">Velis Browser</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-6"
          >
            <a href="#features" className="hidden md:block text-[#94a3b8] hover:text-white transition-colors font-medium">Features</a>
            <a href="#how-it-works" className="hidden md:block text-[#94a3b8] hover:text-white transition-colors font-medium">How It Works</a>
            <Link to="/contact" className="hidden md:block text-[#94a3b8] hover:text-white transition-colors font-medium">Contact</Link>
            <a
              href={DOWNLOAD_LINK}
              onClick={handleDownloadClick}
              className="group bg-gradient-to-r from-[#3b82f6] to-[#1d4ed8] text-white px-6 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-[#3b82f6]/30 transition-all flex items-center gap-2"
            >
              Download
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 px-6 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#3b82f6]/10 border border-[#3b82f6]/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-[#93c5fd] mb-8">
                <Sparkles className="w-4 h-4" />
                <span>Privacy-first browser. Free. Forever.</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6"
            >
              Browse without
              <br />
              <span className="bg-gradient-to-r from-[#60a5fa] via-[#3b82f6] to-[#2563eb] bg-clip-text text-transparent">
                the noise.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-[#94a3b8] max-w-xl mb-10 leading-relaxed font-light"
            >
              Velis is a clean, private browser that blocks ads and trackers,
              upgrades every connection to HTTPS, and clears your data when you close it.
              No noise. No clutter. Just the web.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 mb-8"
            >
              <a
                href={DOWNLOAD_LINK}
                onClick={handleDownloadClick}
                className="group relative bg-gradient-to-r from-[#3b82f6] to-[#1d4ed8] text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all flex items-center gap-3 overflow-hidden hover:shadow-xl hover:shadow-[#3b82f6]/30"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#60a5fa] to-[#3b82f6] opacity-0 group-hover:opacity-100 transition-opacity" />
                <Download className="w-5 h-5 relative z-10" />
                <span className="relative z-10">Download for Windows</span>
                <span className="text-white/70 text-sm font-normal relative z-10">— Free</span>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="space-y-2"
            >
              <p className="text-[#94a3b8] text-sm flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                Windows 10/11 • No sign-up required • 100% free
              </p>
            </motion.div>
          </div>

          {/* Right column — browser preview */}
          <motion.div
            initial={{ opacity: 0, x: 60, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-gradient-to-br from-[#3b82f6]/20 via-[#1d4ed8]/15 to-[#1e40af]/20 rounded-[2.5rem] blur-3xl" />

            <div className="relative bg-gradient-to-b from-white/[0.05] to-white/[0.02] rounded-2xl border border-white/10 p-1.5 backdrop-blur-xl">
              <div className="bg-[#0a121b] rounded-xl overflow-hidden">
                {/* Browser chrome */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#0f1923]">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#1a2735] hover:bg-red-500/60 transition-colors" />
                    <div className="w-3 h-3 rounded-full bg-[#1a2735] hover:bg-yellow-500/60 transition-colors" />
                    <div className="w-3 h-3 rounded-full bg-[#1a2735] hover:bg-green-500/60 transition-colors" />
                  </div>
                  <span className="text-[#94a3b8] text-xs font-medium">Velis Browser</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-[#3b82f6]/40" />
                  </div>
                </div>

                {/* Tab bar */}
                <div className="flex items-center gap-1 px-3 pt-2 bg-[#0f1923]">
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#1a2735] rounded-t-lg border-t border-x border-white/5 min-w-0">
                    <Shield className="w-3 h-3 text-[#3b82f6] shrink-0" />
                    <span className="text-[#e2e8f0] text-xs truncate">New Tab</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 text-[#94a3b8]/60">
                    <span className="text-xs">+</span>
                  </div>
                </div>

                {/* Address bar */}
                <div className="px-3 pt-2 pb-3 bg-[#1a2735] border-b border-white/5">
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#0f1923] rounded-lg border border-white/5">
                    <Lock className="w-3.5 h-3.5 text-[#60a5fa]" />
                    <span className="text-[#94a3b8] text-xs flex-1">velisbrowser.com</span>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-[#3b82f6]/15 border border-[#3b82f6]/30 rounded text-[10px] text-[#93c5fd] font-medium">
                      <ShieldCheck className="w-3 h-3" />
                      <span>14</span>
                    </div>
                  </div>
                </div>

                {/* New tab content */}
                <div className="p-8 min-h-[320px] flex flex-col items-center justify-center">
                  <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-[#3b82f6]/20 to-[#1d4ed8]/20 rounded-2xl flex items-center justify-center border border-[#3b82f6]/25">
                    <Shield className="w-8 h-8 text-[#60a5fa]" />
                  </div>

                  {/* Search */}
                  <div className="w-full max-w-sm mx-auto mb-6">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a2735] rounded-full border border-white/5">
                      <Search className="w-4 h-4 text-[#94a3b8]" />
                      <span className="text-[#94a3b8]/70 text-xs flex-1">Search the web privately</span>
                    </div>
                  </div>

                  {/* Quick links */}
                  <div className="w-full max-w-sm mx-auto grid grid-cols-4 gap-2.5 mb-6">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="aspect-square rounded-lg bg-[#1a2735] border border-white/5 flex items-center justify-center">
                        <div className="w-5 h-5 rounded bg-[#3b82f6]/30" />
                      </div>
                    ))}
                  </div>

                  {/* Trending row */}
                  <div className="w-full max-w-sm mx-auto space-y-1.5">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-3.5 h-3.5 text-[#94a3b8]" />
                      <span className="text-[#94a3b8] text-[11px] font-medium uppercase tracking-wider">Trending</span>
                    </div>
                    <div className="flex items-center gap-3 px-3 py-2 bg-[#1a2735]/60 rounded-lg border border-white/5">
                      <div className="w-1 h-1 rounded-full bg-[#3b82f6]" />
                      <span className="text-[#e2e8f0]/80 text-[11px] flex-1 truncate">Web encryption sees record adoption</span>
                    </div>
                    <div className="flex items-center gap-3 px-3 py-2 bg-[#1a2735]/60 rounded-lg border border-white/5">
                      <div className="w-1 h-1 rounded-full bg-[#3b82f6]" />
                      <span className="text-[#e2e8f0]/80 text-[11px] flex-1 truncate">Privacy by default — the new browser standard</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Protections */}
      <section className="relative z-10 py-20 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Protection that works out of the box
            </h2>
            <p className="text-[#94a3b8] text-lg max-w-2xl mx-auto">
              No setup. No configuration. Velis ships with privacy on, on day one.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {protections.map((p, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group p-5 rounded-xl bg-[#1a2735]/60 border border-white/5 hover:border-[#3b82f6]/30 hover:bg-[#3b82f6]/[0.04] transition-all duration-300 cursor-default"
              >
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#3b82f6]/15 to-[#1d4ed8]/15 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform text-[#60a5fa]">
                  {p.icon}
                </div>
                <h3 className="font-semibold mb-1 text-sm group-hover:text-[#93c5fd] transition-colors">{p.label}</h3>
                <p className="text-[#94a3b8]/70 text-xs">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative z-10 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#3b82f6]/15 via-[#1d4ed8]/15 to-[#1e40af]/15 rounded-3xl border border-[#3b82f6]/15 p-10"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#93c5fd] to-[#3b82f6] bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-[#94a3b8] font-medium text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why people switch to Velis
            </h2>
            <p className="text-[#94a3b8] text-lg max-w-2xl mx-auto font-light">
              Everything a modern browser should be — fast, private, and respectful of your time.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-5"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative p-8 rounded-2xl bg-[#1a2735]/50 border border-white/5 hover:border-[#3b82f6]/20 transition-all duration-500 overflow-hidden flex gap-6 items-start"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] opacity-0 group-hover:opacity-[0.04] transition-opacity duration-500" />

                <div className="relative shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#3b82f6]/20 text-white">
                  {feature.icon}
                </div>
                <div className="relative">
                  <h3 className="text-lg font-bold mb-2 text-white group-hover:text-[#dbeafe] transition-colors">{feature.title}</h3>
                  <p className="text-[#94a3b8] leading-relaxed group-hover:text-[#cbd5e1] transition-colors">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Shield showcase */}
      <section className="relative z-10 py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/20 text-[#93c5fd] text-xs font-semibold uppercase tracking-wider mb-5">
              <Shield className="w-3.5 h-3.5" />
              Toolbar Shield
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              See exactly what's
              <br />
              <span className="bg-gradient-to-r from-[#60a5fa] to-[#3b82f6] bg-clip-text text-transparent">
                being blocked.
              </span>
            </h2>
            <p className="text-[#94a3b8] text-lg leading-relaxed mb-6">
              The shield icon in your toolbar is always live. One glance tells you how
              many trackers and ads Velis stopped on the page you're viewing — and you
              can drill in to see every script, cookie, and connection it caught.
            </p>
            <ul className="space-y-3">
              {[
                'Live counter of blocked trackers per page',
                'Per-site rules — whitelist anything in one click',
                'Detailed breakdown of what was stopped',
                'Total privacy stats since you installed',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-[#cbd5e1]">
                  <CheckCircle2 className="w-5 h-5 text-[#3b82f6] shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-[#3b82f6]/15 to-[#1d4ed8]/10 rounded-3xl blur-2xl" />
            <div className="relative bg-[#1a2735] rounded-2xl border border-white/10 p-6 shadow-2xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">Protection on this page</div>
                  <div className="text-[#94a3b8] text-xs">velisbrowser.com</div>
                </div>
                <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#3b82f6]/15 border border-[#3b82f6]/30">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#60a5fa] animate-pulse" />
                  <span className="text-[10px] font-semibold text-[#93c5fd]">ACTIVE</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { label: 'Trackers', value: '14', icon: <EyeOff className="w-4 h-4" /> },
                  { label: 'Ads', value: '6', icon: <Layers className="w-4 h-4" /> },
                  { label: 'Cookies', value: '23', icon: <Trash2 className="w-4 h-4" /> },
                ].map((item, i) => (
                  <div key={i} className="bg-[#0f1923] rounded-xl border border-white/5 p-4 text-center">
                    <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center text-[#60a5fa]">
                      {item.icon}
                    </div>
                    <div className="text-2xl font-bold text-white">{item.value}</div>
                    <div className="text-[#94a3b8] text-[11px] uppercase tracking-wider mt-0.5">{item.label}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                {[
                  { src: 'doubleclick.net', kind: 'Tracker' },
                  { src: 'google-analytics.com', kind: 'Analytics' },
                  { src: 'facebook.net', kind: 'Tracker' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-[#0f1923] rounded-lg border border-white/5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#3b82f6]" />
                      <span className="text-[#cbd5e1] text-xs font-mono truncate">{row.src}</span>
                    </div>
                    <span className="text-[#94a3b8] text-[10px] font-semibold uppercase tracking-wider shrink-0 ml-3">{row.kind}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">How It Works</h2>
            <p className="text-[#94a3b8] text-lg max-w-2xl mx-auto">
              From download to private browsing in four simple steps.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6 relative"
          >
            <div className="hidden md:block absolute top-[4.5rem] left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-[#3b82f6]/40 via-[#60a5fa]/40 to-[#3b82f6]/40" />

            {howItWorks.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative text-center"
              >
                <div className="relative z-10 w-[5.5rem] h-[5.5rem] mx-auto mb-6 rounded-full bg-[#0f1923] border-2 border-[#3b82f6]/40 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#3b82f6]/15 to-[#1d4ed8]/15 flex items-center justify-center text-[#60a5fa]">
                    {item.icon}
                  </div>
                </div>
                <div className="text-[#60a5fa] text-xs font-bold tracking-widest uppercase mb-2">Step {item.step}</div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-[#94a3b8] leading-relaxed text-sm">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* System Requirements */}
      <section className="relative z-10 py-20 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">System Requirements</h2>
            <p className="text-[#94a3b8]">Velis runs on any modern Windows PC</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {systemRequirements.map((req, index) => (
              <div key={index} className="p-5 rounded-2xl bg-[#1a2735]/60 border border-white/5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-[#3b82f6]/10 flex items-center justify-center text-[#60a5fa]">
                  {req.icon}
                </div>
                <p className="text-[#cbd5e1] text-sm">{req.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-[2.5rem] overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#1d4ed8] via-[#3b82f6] to-[#1e40af]" />
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
                backgroundSize: '24px 24px',
              }}
            />
            <div className="absolute top-0 right-0 w-72 h-72 bg-white/15 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-56 h-56 bg-black/30 rounded-full blur-3xl" />

            <div className="relative p-12 md:p-20 text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Take back your browser.
              </h2>
              <p className="text-xl text-white/85 mb-10 max-w-2xl mx-auto font-light">
                Download Velis today. The clean, private browser for people who'd rather
                not be tracked, profiled, or interrupted.
              </p>
              <a
                href={DOWNLOAD_LINK}
                onClick={handleDownloadClick}
                className="group inline-flex items-center gap-3 bg-white text-[#0f1923] px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white/95 transition-all shadow-2xl hover:shadow-white/20"
              >
                <Download className="w-6 h-6" />
                Download Free for Windows
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <p className="mt-6 text-white/70 text-sm">
                Windows 10/11 • 100% Free • No account required
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold">Velis Browser</span>
          </div>

          <div className="flex gap-8 text-sm text-[#94a3b8]">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>

          <p className="text-[#94a3b8]/70 text-sm">
            © {new Date().getFullYear()} Mild-Ads LTD
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
